import React from 'react';
import PropTypes from 'prop-types';

import get from 'lodash/get';
import Box from '@material-ui/core/Box';
import ListItem from '@material-ui/core/ListItem';

import LocationsCard from 'bento-ordering/locations/components/LocationCard';
import ScrollIntoView from 'bento-ordering/base/components/ScrollIntoView';
import LocationListSection from 'bento-ordering/locations/components/LocationListSection';
import { fulfillmentOptions } from 'bento-ordering/cart/constants/fulfillmentOptions';
import { LOCATION_LIST_PROPTYPES } from 'bento-ordering/base/constants/propTypes';
import { getIsLocationAcceptingOrdersForMethodSelector } from 'bento-ordering/locations/selectors';
import isLocationClosed from 'bento-ordering/locations/utils/isLocationClosed';

const SCROLL_TO_ID = 'locations-list-container';
class LocationsList extends React.Component {
  static propTypes = {
    restaurantLocations: LOCATION_LIST_PROPTYPES,
    locationsThatDeliverToAddress: LOCATION_LIST_PROPTYPES,
    locationsForPickup: LOCATION_LIST_PROPTYPES,
    closedLocations: LOCATION_LIST_PROPTYPES,
    beginOrder: PropTypes.func.isRequired,
    selectedMapLocation: PropTypes.number
  };

  static defaultProps = {
    restaurantLocations: [],
    locationsThatDeliverToAddress: [],
    locationsForPickup: [],
    closedLocations: [],
    selectedMapLocation: 0
  };

  renderLocations = locations => {
    const { selectedMapLocation } = this.props;

    return locations.map((location, index, arr) => {
      const locationDetail = location.location;

      const isSelected = `${location.id}` === `${selectedMapLocation}`;
      const isLast = index === arr.length - 1;
      const cardWrapperMargin = isLast ? 1.25 : 1.75;

      return (
        <Box
          mb={cardWrapperMargin}
          component={ListItem}
          key={location.id}
          data-testname="location-list-item"
        >
          <ScrollIntoView shouldScrollIntoView={isSelected}>
            <LocationsCard
              id={location.id}
              isClosed={isLocationClosed(location)}
              name={locationDetail.name}
              distance={
                location.distance_to_address
                  ? `${location.distance_to_address} miles`
                  : ''
              }
              description={location.description}
              phoneNumber={locationDetail.phone_number}
              street={locationDetail.street}
              city={locationDetail.city}
              state={locationDetail.state}
              postalCode={locationDetail.postal_code}
              isAcceptingPickupOrders={getIsLocationAcceptingOrdersForMethodSelector(
                location,
                fulfillmentOptions.PICKUP
              )}
              isAcceptingDeliveryOrders={getIsLocationAcceptingOrdersForMethodSelector(
                location,
                fulfillmentOptions.DELIVERY
              )}
              isAcceptingDineInOrders={getIsLocationAcceptingOrdersForMethodSelector(
                location,
                fulfillmentOptions.DINE_IN
              )}
              openDisplayPickupHours={location.open_pickup_hours}
              openDisplayDeliveryHours={location.open_delivery_hours}
              openDisplayDineInHours={location.open_dine_in_hours}
              lat={locationDetail.lat}
              lng={locationDetail.lng}
              isSelected={isSelected}
              beginOrder={fulfillmentMethod => {
                this.props.beginOrder(location.id, fulfillmentMethod);
              }}
              isAsapPickupCurrentlyAvailable={get(
                location,
                'fulfillment_options.pickup.is_asap_available'
              )}
              isAsapDeliveryCurrentlyAvailable={get(
                location,
                'fulfillment_options.delivery.is_asap_available'
              )}
              isAsapDineInCurrentlyAvailable={get(
                location,
                'fulfillment_options.dine_in.is_asap_available'
              )}
              firstAvailablePickupDateOption={get(
                location,
                'fulfillment_options.pickup.datetimes_with_ranges[0].date'
              )}
              firstAvailableDeliveryDateOption={get(
                location,
                'fulfillment_options.delivery.datetimes_with_ranges[0].date'
              )}
              firstAvailableDineInDateOption={get(
                location,
                'fulfillment_options.dine_in.datetimes_with_ranges[0].date'
              )}
              doesDeliverToAddress={
                // only pass through the value if it exists on the location objects
                // otherwise delivery will be disabled by default
                location.hasOwnProperty('does_deliver_to_address')
                  ? location.does_deliver_to_address
                  : true
              }
            />
          </ScrollIntoView>
        </Box>
      );
    });
  };

  renderSortedLocations = () => {
    const {
      locationsThatDeliverToAddress,
      locationsForPickup,
      closedLocations
    } = this.props;
    return (
      <>
        {!!locationsThatDeliverToAddress.length && (
          <LocationListSection
            title="Delivers to your address"
            dataTestId="delivery-locations"
          >
            {this.renderLocations(locationsThatDeliverToAddress)}
          </LocationListSection>
        )}

        {!!locationsForPickup.length && (
          <LocationListSection
            title="Pickup locations"
            dataTestId="pickup-locations"
          >
            {this.renderLocations(locationsForPickup)}
          </LocationListSection>
        )}
        {!!closedLocations.length && (
          <LocationListSection
            title="Other locations"
            dataTestId="closed-locations"
          >
            {this.renderLocations(closedLocations)}
          </LocationListSection>
        )}
      </>
    );
  };

  render() {
    const {
      restaurantLocations,
      locationsThatDeliverToAddress,
      locationsForPickup
    } = this.props;

    return (
      <Box mt={3} id={SCROLL_TO_ID}>
        {/* we know we need to render sorted locations if there are any locationsThatDeliverToAddress or locationsForPickup */}
        {locationsThatDeliverToAddress.length || locationsForPickup.length ? (
          this.renderSortedLocations()
        ) : (
          <LocationListSection
            title="All Locations"
            dataTestId="unsorted-locations"
          >
            {this.renderLocations(restaurantLocations)}
          </LocationListSection>
        )}
      </Box>
    );
  }
}

export default LocationsList;
